import React from "react";
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Fade,
    alpha,
    Tooltip,
    ClickAwayListener,
    Popper,
} from "@mui/material";

import {
    Cancel,
    NotificationsActive,
    DeleteSweep,
    CheckCircle,
    Circle
} from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import {
    selectNotificationState,
    clearNotifications,
    markAsRead,
} from "../../../state/notification/notificationSlice";

const NotificationPanel = ({ open, anchorEl, onClose }) => {
    const dispatch = useDispatch();
    const { list } = useSelector(selectNotificationState);

    if (!anchorEl) return null;

    const handleMarkRead = (id) => dispatch(markAsRead(id));
    const handleClearAll = () => dispatch(clearNotifications());

    const formatType = (type) => {
        switch (type) {
            case "admin.broadcast":
                return "System Message";
            case "call.analyzed":
                return "Call Processed";
            case "system.alert":
                return "System Alert";
            default:
                return "Notification";
        }
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            modifiers={[{ name: "offset", options: { offset: [18, 14] } }]}
            sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={150}>
                    <Box>
                        <ClickAwayListener onClickAway={onClose}>
                            <Paper
                                elevation={12}
                                sx={(theme) => ({
                                    width: 340,
                                    maxHeight: 420,
                                    overflow: "hidden",
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                    backdropFilter: "blur(10px)",
                                })}
                            >
                                {/* Header */}
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderBottom: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography variant="h6">Notifications</Typography>

                                    <Box>
                                        {list.length > 0 && (
                                            <Tooltip title="Clear All" arrow>
                                                <IconButton size="small" onClick={handleClearAll}>
                                                    <DeleteSweep />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Close" arrow>
                                            <IconButton size="small" onClick={onClose}>
                                                <Cancel />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Notification List */}
                                <Box
                                    sx={(theme) => ({
                                        maxHeight: 350,
                                        overflowY: "auto",
                                        "&::-webkit-scrollbar": { width: "6px" },
                                        "&::-webkit-scrollbar-track": {
                                            backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                                            borderRadius: "4px",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: alpha(theme.palette.text.secondary, 0.5),
                                            borderRadius: "4px",
                                            "&:hover": {
                                                backgroundColor: alpha(theme.palette.text.secondary, 0.8),
                                            },
                                        },
                                    })}
                                >
                                    {list.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            sx={{ p: 3, textAlign: "center", opacity: 0.7 }}
                                        >
                                            No notifications
                                        </Typography>
                                    ) : (
                                        <List dense>
                                            {list.map((n) => (
                                                <React.Fragment key={n.id}>
                                                    <ListItem
                                                        alignItems="center"
                                                        sx={{
                                                            px: 2,
                                                            py: 1.5,
                                                            cursor: "pointer",
                                                            "&:hover": { backgroundColor: alpha("#fff", 0.06) },
                                                        }}
                                                        onClick={() => handleMarkRead(n.id)}
                                                    >

                                                        <Box sx={{ width: 16, display: "flex", justifyContent: "center" }}>
                                                            {!n.read ? (
                                                                <Circle sx={{ color: "secondary.main", fontSize: 10 }} />
                                                            ) : (
                                                                <CheckCircle sx={{ color: "success.main", fontSize: 12 }} />
                                                            )}
                                                        </Box>

                                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                                            <NotificationsActive sx={{ mr: 1 }} />
                                                        </Box>

                                                        <ListItemText
                                                            primary={n.message}
                                                            secondary={
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatType(n.type)} • {new Date(n.timestamp).toLocaleString()}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                    <Divider component="li" />
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            </Paper>
                        </ClickAwayListener>
                    </Box>
                </Fade>
            )}
        </Popper>
    );
};

export default NotificationPanel;
